import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlatformRulesConfig from './components/PlatformRulesConfig';
import FeeStructureManagement from './components/FeeStructureManagement';
import VerificationRequirements from './components/VerificationRequirements';
import PolicyManagement from './components/PolicyManagement';
import { getPageMetaTags } from '../../utils/metaTags';
import platformConfigService from '../../services/platformConfigService';

const AdminSettings = () => {
  const { user, isAuthenticated, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rules');
  const [saveStatus, setSaveStatus] = useState(null);
  const [configData, setConfigData] = useState({
    rules: null,
    fees: null,
    verification: null,
    policies: []
  });
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
    if (!loading && isAuthenticated && !isAdmin) {
      navigate('/home-page');
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  // Load configuration data from database
  useEffect(() => {
    if (isAdmin) {
      loadConfigData();
    }
  }, [isAdmin]);

  const loadConfigData = async () => {
    setLoadingConfig(true);
    try {
      const [rules, fees, verification, policies] = await Promise.all([
        platformConfigService?.getPlatformRules(),
        platformConfigService?.getFeeStructure(),
        platformConfigService?.getVerificationRequirements(),
        platformConfigService?.getPolicyTemplates()
      ]);

      setConfigData({ rules, fees, verification, policies });
    } catch (error) {
      console.error('Failed to load configuration:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  if (loading || loadingConfig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        {getPageMetaTags('admin-settings')}
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="font-body text-muted-foreground">Loading admin settings...</span>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        {getPageMetaTags('admin-settings')}
        <div className="text-center">
          <Icon name="ShieldAlert" size={48} className="text-error mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="font-body text-muted-foreground mb-6">You do not have permission to access this page.</p>
          <Button onClick={() => navigate('/home-page')}>Return to Home</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'rules', label: 'Platform Rules', icon: 'FileText' },
    { id: 'fees', label: 'Fee Structure', icon: 'DollarSign' },
    { id: 'verification', label: 'Verification', icon: 'ShieldCheck' },
    { id: 'policies', label: 'Policy Management', icon: 'BookOpen' }
  ];

  const handleSaveAll = async () => {
    setSaveStatus('saving');
    try {
      await loadConfigData();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {getPageMetaTags('admin-settings')}
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={24} strokeWidth={2} className="text-primary" />
                </div>
                <div>
                  <h1 className="font-heading text-3xl font-bold text-foreground">Admin Settings</h1>
                  <p className="font-body text-muted-foreground">Platform configuration and policy management</p>
                </div>
              </div>
              <Button
                onClick={handleSaveAll}
                loading={saveStatus === 'saving'}
                variant={saveStatus === 'success' ? 'success' : 'default'}
                iconName={saveStatus === 'success' ? 'Check' : 'Save'}
                aria-label="Save all configuration changes"
              >
                {saveStatus === 'success' ? 'Saved' : 'Save All Changes'}
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-lg shadow-elevation-2 mb-6 overflow-x-auto">
            <div className="flex space-x-1 p-2 min-w-max" role="tablist" aria-label="Admin settings sections">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  role="tab"
                  aria-selected={activeTab === tab?.id}
                  aria-controls={`${tab?.id}-panel`}
                  tabIndex={activeTab === tab?.id ? 0 : -1}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-md font-body font-medium transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} strokeWidth={2} aria-hidden="true" />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6" role="tabpanel" id={`${activeTab}-panel`} aria-labelledby={activeTab}>
            {activeTab === 'rules' && <PlatformRulesConfig data={configData?.rules} onUpdate={loadConfigData} />}
            {activeTab === 'fees' && <FeeStructureManagement data={configData?.fees} onUpdate={loadConfigData} />}
            {activeTab === 'verification' && <VerificationRequirements data={configData?.verification} onUpdate={loadConfigData} />}
            {activeTab === 'policies' && <PolicyManagement data={configData?.policies} onUpdate={loadConfigData} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;